import { ChangeDetectionStrategy, Component, computed, HostListener, input, output } from '@angular/core';
import { DateAdapter } from '../adapters/abstract-date-adapter';
import { HxCalendarBody } from './calendar-body';
import { CalendarCell } from './calendar-cell';

/** Multi-year view component displaying 24 years in a 4x6 grid. Delegates rendering to CalendarBody. */
@Component({
  selector: 'hx-multi-year-view',
  standalone: true,
  imports: [HxCalendarBody],
  template: `
    <hx-calendar-body
      [activeCell]="activeCellIndex()"
      [cellAspectRatio]="7 / 4"
      [label]="rangeLabel()"
      [numCols]="3"
      [rows]="yearRows()"
      [selectedCell]="selectedCellIndex()"
      (activeDateChange)="activeDateChange.emit($event)"
      (selectedChange)="selectedChange.emit($event)" />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxMultiYearView<D = Date> {
  readonly dateAdapter = input.required<DateAdapter<D>>();
  readonly activeDate = input.required<D>();
  readonly selected = input<D | null>(null);
  readonly minDate = input<D | null>(null);
  readonly maxDate = input<D | null>(null);
  readonly allowedYears = input<number[] | null>(null);

  readonly selectedChange = output<D>();
  readonly activeDateChange = output<D>();

  private readonly YEARS_PER_PAGE = 12;
  private readonly YEARS_PER_ROW = 3;

  protected readonly rangeLabel = computed(() => {
    const adapter = this.dateAdapter();
    const activeYear = adapter.getYear(this.activeDate());
    const startYear = Math.floor(activeYear / this.YEARS_PER_PAGE) * this.YEARS_PER_PAGE;
    const endYear = startYear + this.YEARS_PER_PAGE - 1;
    return `${startYear} \u2013 ${endYear}`;
  });

  protected readonly yearRows = computed(() => {
    const adapter = this.dateAdapter();
    const activeYear = adapter.getYear(this.activeDate());
    const startYear = Math.floor(activeYear / this.YEARS_PER_PAGE) * this.YEARS_PER_PAGE;
    const today = adapter.today();
    const currentYear = adapter.getYear(today);

    const rows: CalendarCell<D>[][] = [];
    let currentRow: CalendarCell<D>[] = [];

    for (let i = 0; i < this.YEARS_PER_PAGE; i++) {
      const year = startYear + i;
      const date = adapter.create(year, 0, 1);
      const enabled = this.isYearEnabled(date);
      const isCurrentYear = year === currentYear;

      currentRow.push({
        value: date,
        displayValue: year.toString(),
        ariaLabel: year.toString(),
        enabled,
        cssClasses: [...(isCurrentYear ? ['hx-calendar-body__cell--today'] : []), 'hx-calendar-body__cell--large'],
      });

      if (currentRow.length === this.YEARS_PER_ROW) {
        rows.push(currentRow);
        currentRow = [];
      }
    }

    return rows;
  });

  protected readonly activeCellIndex = computed(() => {
    const adapter = this.dateAdapter();
    const activeYear = adapter.getYear(this.activeDate());
    const startYear = Math.floor(activeYear / this.YEARS_PER_PAGE) * this.YEARS_PER_PAGE;
    return activeYear - startYear;
  });

  protected readonly selectedCellIndex = computed(() => {
    const selected = this.selected();
    if (!selected) {
      return null;
    }

    const adapter = this.dateAdapter();
    const selectedYear = adapter.getYear(selected);
    const activeYear = adapter.getYear(this.activeDate());
    const startYear = Math.floor(activeYear / this.YEARS_PER_PAGE) * this.YEARS_PER_PAGE;
    const endYear = startYear + this.YEARS_PER_PAGE - 1;

    if (selectedYear < startYear || selectedYear > endYear) {
      return null;
    }

    return selectedYear - startYear;
  });

  private isYearEnabled(date: D): boolean {
    const adapter = this.dateAdapter();
    const min = this.minDate();
    const max = this.maxDate();
    const years = this.allowedYears();

    const year = adapter.getYear(date);

    // Check allowed years first
    if (years && years.length > 0) {
      if (!years.includes(year)) {
        return false;
      }
    }

    if (min && year < adapter.getYear(min)) {
      return false;
    }

    if (max && year > adapter.getYear(max)) {
      return false;
    }

    return true;
  }

  /** Handle keyboard navigation within the multi-year view. */
  @HostListener('keydown', ['$event'])
  protected handleKeydown(event: KeyboardEvent): void {
    const adapter = this.dateAdapter();
    const active = this.activeDate();
    let newDate: D | null = null;

    switch (event.key) {
      // Arrow navigation (4 columns)
      case 'ArrowLeft':
        newDate = adapter.addCalendarYears(active, -1);
        break;
      case 'ArrowRight':
        newDate = adapter.addCalendarYears(active, 1);
        break;
      case 'ArrowUp':
        newDate = adapter.addCalendarYears(active, -4);
        break;
      case 'ArrowDown':
        newDate = adapter.addCalendarYears(active, 4);
        break;

      // Page boundaries (±24 years)
      case 'Home':
        const startYear = Math.floor(adapter.getYear(active) / this.YEARS_PER_PAGE) * this.YEARS_PER_PAGE;
        newDate = adapter.create(startYear, 0, 1);
        break;
      case 'End':
        const endYear =
          Math.floor(adapter.getYear(active) / this.YEARS_PER_PAGE) * this.YEARS_PER_PAGE + this.YEARS_PER_PAGE - 1;
        newDate = adapter.create(endYear, 11, 31);
        break;
      case 'PageUp':
        newDate = adapter.addCalendarYears(active, -this.YEARS_PER_PAGE);
        break;
      case 'PageDown':
        newDate = adapter.addCalendarYears(active, this.YEARS_PER_PAGE);
        break;

      // Selection
      case 'Enter':
      case ' ':
        this.selectedChange.emit(active);
        event.preventDefault();
        return;

      default:
        return;
    }

    if (newDate) {
      const min = this.minDate();
      const max = this.maxDate();

      if (min && adapter.compareDate(newDate, min) < 0) {
        newDate = min;
      }
      if (max && adapter.compareDate(newDate, max) > 0) {
        newDate = max;
      }

      this.activeDateChange.emit(newDate);
      event.preventDefault();
    }
  }
}
